<template>
  <div class="h-100">
    <h1>Pagamentos</h1>
    <div
      v-if="loading"
      class="d-flex justify-content-center m-2"
    >
      <b-spinner variant="primary" />
    </div>
    <template v-else-if="items.length > 0">
      <section
        class="payment-overview m-2"
        aria-label="Resumo financeiro do contrato"
      >
        <div class="overview-card overview-card--expected">
          <span
            class="overview-card__icon"
            aria-hidden="true"
          >
            <feather-icon
              icon="CalendarIcon"
              size="20"
            />
          </span>
          <div>
            <span class="overview-card__label">Total previsto</span>
            <strong class="overview-card__value">{{ paymentSummary.expected | toCurrency }}</strong>
            <small>Parcelas ativas do contrato</small>
          </div>
        </div>
        <div class="overview-card overview-card--paid">
          <span
            class="overview-card__icon"
            aria-hidden="true"
          >
            <feather-icon
              icon="CheckCircleIcon"
              size="20"
            />
          </span>
          <div>
            <span class="overview-card__label">Total pago</span>
            <strong class="overview-card__value">{{ paymentSummary.paid | toCurrency }}</strong>
            <small>Valores efetivamente reconciliados</small>
          </div>
        </div>
        <div class="overview-progress">
          <div class="overview-progress__header">
            <div>
              <span class="overview-card__label">Progresso de pagamento</span>
              <small>{{ paidInstallments }} de {{ activeInstallments }} parcelas pagas</small>
            </div>
            <strong>{{ formattedPercentage }}%</strong>
          </div>
          <div
            class="premium-progress"
            role="progressbar"
            aria-label="Progresso de pagamento"
            aria-valuemin="0"
            aria-valuemax="100"
            :aria-valuenow="Math.round(installmentProgress)"
          >
            <span :style="{ width: `${installmentProgress}%` }" />
          </div>
        </div>
      </section>
      <b-card
        no-body
        class="mb-0 m-2 h-100 pb-3"
      >
        <b-table
          :items="items"
          striped
          borderless
          responsive
          :fields="tableColumns"
          show-empty
          empty-text="Não foram encontrados pagamentos"
          :busy="loading"
          class="scroll-table"
        >
          <!-- Column: status -->
          <template #cell(status)="data">
            <div class="text-wrap">
              <span class="align-text-top text-capitalize">{{
                data.item.status_description
              }}</span>
            </div>
          </template>
          <!-- Column: installment -->
          <template #cell(installment)="data">
            <div class="text-wrap">
              <span class="align-text-top text-capitalize">{{
                data.item.installment
              }}</span>
            </div>
          </template>
          <!-- Column: amount header -->
          <template #head(amount)="data">
            <span class="d-inline-flex align-items-center">
              {{ data.label }}
              <feather-icon
                v-b-tooltip.hover.bottom="{
                  customClass: 'amount-info-tooltip',
                  boundary: 'window',
                  title:
                    'Valor bruto contratual da parcela; o líquido (com 15% de imposto) é usado nos cálculos financeiros.',
                }"
                icon="InfoIcon"
                size="14"
                class="amount-info-icon cursor-pointer ml-50"
              />
            </span>
          </template>
          <!-- Column: amount (exibe o bruto contratual; fallback p/ amount se gross_amount ausente) -->
          <template #cell(amount)="data">
            <div class="text-wrap">
              <span class="align-text-top text-capitalize">{{
                (data.item.gross_amount != null
                  ? data.item.gross_amount
                  : data.item.amount) | toCurrency
              }}</span>
            </div>
          </template>
          <!-- Column: expiration_date -->
          <template #cell(expiration_date)="data">
            <div class="text-wrap">
              <span
                v-if="data.item.payment_date"
                class="align-text-top text-capitalize"
              >{{ data.item.expiration_date | dateToStr }}</span>
            </div>
          </template>
          <!-- Column: reconciled_date -->
          <template #cell(reconciled_date)="data">
            <div class="text-wrap">
              <span
                v-if="data.item.reconciled_date"
                class="align-text-top text-capitalize"
              >{{ data.item.reconciled_date | dateTime }}</span>
            </div>
          </template>
          <!-- Column: reconciled_amount -->
          <template #cell(reconciled_amount)="data">
            <div class="text-wrap">
              <span
                v-if="data.item.reconciled_date"
                class="align-text-top text-capitalize"
              >{{ data.item.reconciled_amount | toCurrency }}</span>
            </div>
          </template>
          <template #cell(conciliator)="data">
            <div class="text-wrap">
              <span class="align-text-top text-capitalize">{{
                data.item.conciliator
              }}</span>
            </div>
          </template>
          <template #cell(transaction)="data">
            <div class="text-wrap">
              <span class="align-text-top text-capitalize">{{
                data.item.transaction_id
              }}</span>
            </div>
          </template>
          <!-- Column: Actions -->
          <template #cell(actions)="data">
            <b-dropdown
              v-if="canUpdateContract && data.item.status == 'paid'"
              variant="link"
              no-caret
              right
            >
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="align-middle text-body"
                />
              </template>
              <!-- <b-dropdown-item @click="remove(data.item.id)">
                <feather-icon icon="TrashIcon" />
                <span class="align-middle ml-50">Remover</span>
              </b-dropdown-item> -->
              <b-dropdown-item @click="reversal(data.item.id)">
                <feather-icon icon="RotateCcwIcon" />
                <span class="align-middle ml-50">Estornar comissões</span>
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
      </b-card>
    </template>
    <template v-else>
      <span class="d-flex justify-content-center">
        Não foram encontrados pagamentos
      </span>
    </template>
  </div>
</template>

<script>
import {
  BCard,
  BTable,
  BDropdown,
  BDropdownItem,
  BSpinner,
} from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import useAppConfig from '@core/app-config/useAppConfig'
import { contractPermissions } from '@/mixins/contractPermissions'
import { apiErrorToast } from '@/helpers/error_messages'
import * as types from '../store/types'
import { getPaymentSummary } from '../utils/contractFinancial'

export default {
  components: {
    BCard,
    BTable,
    BDropdown,
    BDropdownItem,
    BSpinner,
  },
  mixins: [contractPermissions],
  setup() {
    const { skin } = useAppConfig()

    return { toast: useToast(), skin }
  },
  data() {
    return {
      loading: false,
    }
  },
  computed: {
    ...mapGetters({
      payments: types.PAYMENTS,
      details: types.DETAILS_GENERAL_INFORMATION,
    }),
    items() {
      return this.payments || []
    },
    paymentSummary() {
      return getPaymentSummary(this.items)
    },
    activeInstallments() {
      return this.items.filter(payment => payment.status !== 'canceled').length
    },
    paidInstallments() {
      return this.items.filter(payment => payment.status === 'paid').length
    },
    installmentProgress() {
      return this.activeInstallments > 0
        ? Math.min((this.paidInstallments / this.activeInstallments) * 100, 100)
        : 0
    },
    formattedPercentage() {
      return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 })
        .format(this.installmentProgress)
    },
    hasTransactions() {
      return this.details?.has_transactions || false
    },
    tableColumns() {
      return [
        { key: 'status', label: 'Status' },
        { key: 'installment', label: 'Parcela' },
        { key: 'amount', label: 'Valor' },
        { key: 'expiration_date', label: 'Data prevista' },
        { key: 'reconciled_date', label: 'Data da reconciliação' },
        { key: 'reconciled_amount', label: 'Valor reconciliado' },
        { key: 'conciliator', label: 'Origem' },
        this.hasTransactions
          ? { key: 'transaction', label: 'ID Transação' }
          : null,
        { key: 'actions', label: 'Ações' },
      ]
    },
  },
  mounted() {
    this.get()
  },
  methods: {
    ...mapActions({
      getPayments: types.GET_PAYMENTS,
      deletePayment: types.DELETE_PAYMENT,
      reversalPayment: types.REVERSAL_PAYMENT,
    }),
    get() {
      this.loading = true
      this.getPayments({
        id: this.$route.params.id,
      })
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Oops!',
              text: 'Ocorreu um erro ao buscar os pagamentos. Entre em contato com o setor de TI.',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    remove(idPayment) {
      this.$swal({
        title: 'Tem certeza?',
        text: 'Deseja remover o pagamento?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Remover',
        cancelButtonText: 'Cancelar',
        background: `${this.skin === 'dark' ? '#283046' : ''}`,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-secondary ml-1',
          htmlContainer: `${this.skin === 'dark' ? 'text-white' : ''}`,
        },
      }).then(result => {
        if (result.value) {
          this.deletePayment({ id: idPayment })
            .then(() => {
              this.get()
              this.toast({
                component: ToastificationContent,
                props: {
                  title: 'Sucesso',
                  text: 'O pagamento foi removido',
                  icon: 'CoffeeIcon',
                  variant: 'success',
                },
              })
            })
            .catch(() => {
              this.toast({
                component: ToastificationContent,
                props: {
                  title: 'Oops!',
                  text: 'Esse pagamento já foi faturado e não pode ser removido. Entre em contato com o setor de TI.',
                  icon: 'AlertTriangleIcon',
                  variant: 'danger',
                },
              })
            })
        }
      })
    },
    reversal(idPayment) {
      this.$swal({
        title: 'Tem certeza?',
        text: 'Essa ação irá criar um ajuste financeiro de estorno para as comissões relacionadas a esse pagamento e não poderá ser desfeita.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Estornar',
        cancelButtonText: 'Cancelar',
        background: `${this.skin === 'dark' ? '#283046' : ''}`,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-secondary ml-1',
          htmlContainer: `${this.skin === 'dark' ? 'text-white' : ''}`,
        },
      }).then(result => {
        if (result.value) {
          this.reversalPayment({ id: idPayment })
            .then(() => {
              this.get()
              this.toast({
                component: ToastificationContent,
                props: {
                  title: 'Sucesso',
                  text: 'As comissões foram estornadas com sucesso',
                  icon: 'CoffeeIcon',
                  variant: 'success',
                },
              })
            })
            .catch(error => {
              this.toast({
                component: ToastificationContent,
                props: apiErrorToast(
                  error,
                  'Ocorreu um erro ao estornar as comissões. Entre em contato com o setor de TI.',
                ),
              })
            })
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.scroll-table {
  overflow-x: scroll;
  overflow-y: auto;
}

.card {
  // box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.24);
  box-shadow: none !important;
}

.amount-info-icon {
  color: #00cfe8 !important; // info (ciano) — destaque mesmo dentro do cabeçalho da tabela
  transition: color 0.15s ease-in-out;

  &:hover {
    color: #1ad6ec !important;
  }
}

.payment-overview {
  display: grid;
  grid-template-columns: minmax(210px, 0.8fr) minmax(210px, 0.8fr) minmax(300px, 1.4fr);
  gap: 1rem;
  animation: overview-enter 0.45s ease-out both;
}

.overview-card,
.overview-progress {
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 1rem;
  min-height: 126px;
  padding: 1.35rem;
  border: 1px solid rgba(110, 107, 123, 0.12);
  border-radius: 0.85rem;
  background: var(--white, #fff);
  box-shadow: 0 8px 26px rgba(34, 41, 47, 0.07);
}

.overview-card::after {
  content: "";
  position: absolute;
  right: -36px;
  bottom: -50px;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.06;
}

.overview-card--expected { color: #7367f0; }
.overview-card--paid { color: #28c76f; }

.overview-card__icon {
  display: grid;
  place-items: center;
  flex: 0 0 46px;
  width: 46px;
  height: 46px;
  border-radius: 14px;
  color: inherit;
  background: currentColor;
  box-shadow: inset 0 0 0 32px rgba(255, 255, 255, 0.88);
}

.overview-card__label,
.overview-card small,
.overview-progress small {
  display: block;
  color: #6e6b7b;
}

.overview-card__value {
  display: block;
  margin: 0.2rem 0;
  color: #2f2b3d;
  font-size: 1.45rem;
  line-height: 1.25;
}

.overview-progress {
  display: block;
  color: #2f2b3d;
}

.overview-progress__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.overview-progress__header strong {
  color: #7367f0;
  font-size: 1.5rem;
}

.premium-progress {
  overflow: hidden;
  height: 12px;
  border-radius: 99px;
  background: rgba(115, 103, 240, 0.12);
}

.premium-progress span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #7367f0, #00cfe8);
  box-shadow: 0 0 16px rgba(115, 103, 240, 0.38);
  transition: width 0.8s cubic-bezier(0.22, 1, 0.36, 1);
}

@keyframes overview-enter {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (max-width: 991.98px) {
  .payment-overview { grid-template-columns: 1fr 1fr; }
  .overview-progress { grid-column: 1 / -1; }
}

@media (max-width: 575.98px) {
  .payment-overview { grid-template-columns: 1fr; }
  .overview-progress { grid-column: auto; }
}

@media (prefers-reduced-motion: reduce) {
  .payment-overview { animation: none; }
  .premium-progress span { transition: none; }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.dark-layout {
  .payment-overview {
    .overview-card,
    .overview-progress {
      border-color: rgba(207, 208, 226, 0.12);
      background: linear-gradient(145deg, #2d3548, #283046);
      box-shadow: 0 10px 28px rgba(10, 13, 22, 0.24);
    }

    .overview-card::after {
      opacity: 0.1;
    }

    .overview-card__label,
    .overview-card small,
    .overview-progress small {
      color: #b4b7bd;
    }

    .overview-card__value,
    .overview-progress {
      color: #f1f1f4;
    }

    .overview-card__icon {
      box-shadow: inset 0 0 0 32px rgba(40, 48, 70, 0.82);
    }

    .premium-progress {
      background: rgba(207, 208, 226, 0.12);
    }
  }
}

// Tooltip de ajuda da coluna "Valor" (maior que o padrão)
.amount-info-tooltip {
  margin-top: 2.0rem; // abre mais abaixo do ícone

  .tooltip-inner {
    width: max-content;
    max-width: 210px;
    font-size: 1rem;
    line-height: 1.45;
    padding: 0.75rem 1rem;
    text-align: left;
  }
}
</style>
